import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { assessmentApi } from '../../services/assessmentApi';

const PIN_STATUS = {
  active: { label: 'PIN set', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  skipped: { label: 'Skipped', className: 'bg-slate-100 text-slate-600 ring-slate-200' },
  locked_out: { label: 'Locked out', className: 'bg-red-50 text-red-700 ring-red-200' },
  removed: { label: 'Removed', className: 'bg-slate-50 text-slate-500 ring-slate-200' },
};

function when(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function DeviceRow({ d, userEnabled }) {
  const meta = PIN_STATUS[d.pinStatus];
  const at = { active: d.pinSetAt, skipped: d.skippedAt, locked_out: d.lockedOutAt, removed: d.removedAt }[d.pinStatus];
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm">
      <span className="text-slate-800">{d.label || 'Unknown device'}</span>
      {meta && (
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.className}`}>{meta.label}</span>
      )}
      {at && <span className="text-xs text-slate-500">{when(at)}</span>}
      {d.pinStatus === 'active' && !userEnabled && (
        <span className="text-xs text-amber-700">� offline is off; removed at next sign-in on this device</span>
      )}
      {d.lastSeenAt && <span className="text-xs text-slate-400">· last used {when(d.lastSeenAt)}</span>}
    </li>
  );
}

// Managers and admins: who may work offline, and on which devices a PIN is set.
// Only admins can switch access on or off.
export default function OfflineAccessPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['offline-access'],
    queryFn: assessmentApi.offlineAccess,
  });
  // Optimistic: the switch moves at once and moves back if the save fails.
  const toggle = useMutation({
    mutationFn: ({ id, enabled }) => assessmentApi.setOfflineAccess(id, enabled),
    onMutate: async ({ id, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ['offline-access'] });
      const previous = queryClient.getQueryData(['offline-access']);
      queryClient.setQueryData(['offline-access'], (old) => old && ({
        ...old, users: old.users.map((u) => (u.id === id ? { ...u, offlineEnabled: enabled } : u)),
      }));
      return { previous };
    },
    onError: (err, vars, ctx) => { if (ctx?.previous) queryClient.setQueryData(['offline-access'], ctx.previous); },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['offline-access'] }),
  });

  const users = useMemo(() => (data?.users || []).filter((u) => !filter
    || `${u.name} ${u.email}`.toLowerCase().includes(filter.toLowerCase())), [data, filter]);
  const withPin = (data?.users || []).filter((u) => u.devices.some((d) => d.pinStatus === 'active')).length;

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900 md:text-2xl">Offline access</h1>
      <p className="mt-1 text-sm text-slate-500">
        Who can work on assessments offline, and the devices where they have set an offline PIN.
        {data && ` ${data.users.filter((u) => u.offlineEnabled).length} enabled · ${withPin} with a PIN set.`}
      </p>
      {data && !data.canEdit && (
        <p className="mt-2 text-xs text-slate-500">Only administrators can change who has offline access.</p>
      )}

      <label className="mt-4 flex max-w-sm items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2">
        <Search className="h-4 w-4 text-slate-400" />
        <input className="w-full text-sm focus:outline-none" placeholder="Search by name or email" value={filter}
          autoComplete="off" onChange={(e) => setFilter(e.target.value)} />
      </label>

      {isLoading && <p className="mt-6 text-sm text-slate-500">Loading…</p>}
      {isError && <p className="mt-6 text-sm text-red-600">{error.message}</p>}
      {toggle.isError && <p className="mt-4 text-sm text-red-600">{toggle.error.message}</p>}

      <ul className="mt-4 space-y-3">
        {users.map((u) => (
          <li key={u.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-slate-900">{u.name}</p>
                <p className="text-xs text-slate-500">{u.email} · {u.role}</p>
              </div>
              {data.canEdit ? (
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" role="switch" className="h-5 w-5 rounded border-slate-300 text-blue-600"
                    aria-label={`Offline access for ${u.name}`}
                    checked={u.offlineEnabled}
                    disabled={toggle.isPending}
                    onChange={(e) => toggle.mutate({ id: u.id, enabled: e.target.checked })} />
                  Offline access
                </label>
              ) : (
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${u.offlineEnabled
                  ? 'bg-blue-50 text-blue-700 ring-blue-200' : 'bg-slate-50 text-slate-500 ring-slate-200'}`}>
                  Offline {u.offlineEnabled ? 'enabled' : 'off'}
                </span>
              )}
            </div>
            {u.devices.length > 0 ? (
              <ul className="mt-2 divide-y divide-slate-100 border-t border-slate-100">
                {u.devices.map((d) => <DeviceRow key={d.deviceId} d={d} userEnabled={u.offlineEnabled} />)}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-slate-400">No devices reported yet.</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
